// What a notification card says beyond its title: the body when it adds something, the claim
// class when the card reports rather than measures, and how many source links it lists.
//
//   title  "Russo-Georgian War: Russian forces entered South Ossetia."
//   body   "Russian forces entered South Ossetia."     → not shown, the title already says it
//   body   "Russia and Georgia fight. The dead are counted."  → shows "The dead are counted."
//   claim  "Unverified · Wikipedia Current events, 8 Aug 2008"   (event bullets only)

import type { DateStr } from '../core/schema.ts';
import type { ClaimKind } from './parts.tsx';
import { fmtDate } from './format.ts';

/** Event bullets are raised with this id prefix (src/services/notifications.ts fromEvent). */
const EVENT_ID = /^evt:\d{4}-\d{2}-\d{2}:/;
const WHITESPACE = /\s+/g;
const PREFIX = /^[^:]{1,80}:\s+/;

export interface NoteClaim {
  kind: ClaimKind;
  text: string;
}

/** Lower-case, single-spaced, so spacing and case do not hide a repeat. */
function flat(text: string): string {
  return text.trim().replace(WHITESPACE, ' ').toLowerCase();
}

/** What follows `head` at the start of `text`, or null when `text` does not start with it. */
function after(text: string, head: string): string | null {
  const start = head.trim();
  if (start === '' || !text.startsWith(start)) {
    return null;
  }

  return text.slice(start.length).trim();
}

/** The body without what the title already says; null when nothing is left. */
export function bodyOf(title: string, body: string): string | null {
  const text = flat(body);
  if (text === '') {
    return null;
  }

  const bare = title.replace(PREFIX, '');
  if (flat(title).includes(text) || flat(bare).includes(text)) {
    return null;
  }

  const whole = body.trim();
  const rest = after(whole, title) ?? after(whole, bare);
  return rest === '' ? null : (rest ?? body);
}

/** The claim class of a card: event bullets are unverified reports; other cards carry none here. */
export function noteClaim(id: string, date: DateStr): NoteClaim | null {
  if (!EVENT_ID.test(id)) {
    return null;
  }

  return { kind: 'unverified', text: `Unverified · Wikipedia Current events, ${fmtDate(date)}` };
}

/** The first `limit` links and how many more there are. */
export function splitLinks<T>(links: readonly T[], limit: number): { shown: T[]; more: number } {
  return { shown: links.slice(0, limit), more: Math.max(0, links.length - limit) };
}

/** How far (px) to raise a card so its bottom clears `limit`, without its top rising above `minTop`. */
export function liftOf(top: number, bottom: number, limit: number, minTop: number): number {
  const over = bottom - limit;
  if (over <= 0) {
    return 0;
  }

  return Math.min(over, Math.max(0, top - minTop));
}
